import { Close } from "@carbon/icons-react";
import { Dispatch, SetStateAction, useState } from "react";
import { useTranslation } from "react-i18next";

import PageLoader from "../../../../../components/PageLoader";
import FallbackComponent from "../../../../../components/fallbackComponent/FallbackComponent";
import { useNewOrderQueryData } from "../../../../../hooks/useNewOrderQueryData";
import { IDeliveryItem } from "../../../../../models/deliveryItem";
import { calcExistingDeliveryItemPositions } from "../../../../../utils/helper";
import AddDeliveryItemDialog from "../../dialogs/AddDeliveryItemDialog";
import DeliveryItemList from "./DeliveryItemList";

interface EditableDeliveryItemsProps {
  deliveryItems: IDeliveryItem[];
  setDeliveryItems: Dispatch<SetStateAction<IDeliveryItem[]>>;
}

const EditableDeliveryItems = (props: EditableDeliveryItemsProps) => {
  const { t } = useTranslation();
  const { queryData, loading, error } = useNewOrderQueryData();

  const { deliveryItems, setDeliveryItems } = props;

  const [showDialog, setShowDialog] = useState(false);
  const [itemToEdit, setItemToEdit] = useState<IDeliveryItem | null>(null);

  const sortedItems = deliveryItems.sort((a, b) => a.position - b.position);

  const onEdit = (item: IDeliveryItem) => {
    setItemToEdit(item);
    setShowDialog(true);
  };

  const onDelete = (id: string) => {
    setDeliveryItems(deliveryItems.filter((addedItem) => addedItem.id !== id));
  };

  const onSaveNewItem = (newItem: IDeliveryItem) => {
    if (itemToEdit) {
      setDeliveryItems(
        deliveryItems.map((item) => (item.id === newItem.id ? newItem : item)),
      );
    } else {
      setDeliveryItems([...deliveryItems, newItem]);
    }
    setShowDialog(false);
    setItemToEdit(null);
  };

  const onCloseDialog = () => {
    setItemToEdit(null);
    setShowDialog(false);
  };

  if (loading) return <PageLoader />;

  if (error || !queryData.materials || !queryData.stockTypes)
    return (
      <FallbackComponent
        middleIcon={<Close size={32} />}
        text={`${t("queryError")}. ${t("tryAgainLater")}`}
      />
    );

  return (
    <div>
      <DeliveryItemList
        items={sortedItems}
        onAdd={() => setShowDialog(true)}
        onEdit={onEdit}
        onDelete={onDelete}
      />
      {showDialog && (
        <AddDeliveryItemDialog
          isOpen={showDialog}
          closeDialog={onCloseDialog}
          onSave={onSaveNewItem}
          itemToEdit={itemToEdit}
          existingPositions={calcExistingDeliveryItemPositions(
            deliveryItems,
            itemToEdit,
          )}
        />
      )}
    </div>
  );
};

export default EditableDeliveryItems;
